import { supabase } from "../supabase";
import { saveLocalActiveSession } from "./activeSession";
import {
  dispatchSessionStateChanged,
  SESSION_STATE_ACTION,
} from "./appSessionStateEvents";
import { devLog } from "./devLog";

/**
 * Start break on an active session. Mirrors TimeTrackingV2Screen button logic.
 * @param {object} activeSession
 * @returns {Promise<{ session: object, offline: boolean }>}
 */
export async function startBreak(activeSession) {
  if (!activeSession) {
    return { session: null, offline: false };
  }

  const breakTimestamp = new Date().toISOString();
  const updatedSession = {
    ...activeSession,
    break_active: true,
    break_start: breakTimestamp,
  };

  saveLocalActiveSession(updatedSession);

  const { error } = await supabase
    .from("active_sessions")
    .update({
      break_active: true,
      break_start: breakTimestamp,
    })
    .eq("session_id", activeSession.session_id);

  if (error) {
    devLog("OFFLINE START BREAK");
    dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.START_BREAK });
    return { session: updatedSession, offline: true };
  }

  const { data } = await supabase
    .from("active_sessions")
    .select("*")
    .eq("session_id", activeSession.session_id)
    .single();

  if (data) {
    saveLocalActiveSession(data);
    dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.START_BREAK });
    return { session: data, offline: false };
  }

  dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.START_BREAK });
  return { session: updatedSession, offline: false };
}

/**
 * Stop break on an active session. Mirrors TimeTrackingV2Screen button logic.
 * @param {object} activeSession
 * @returns {Promise<{ session: object, offline: boolean }>}
 */
export async function stopBreak(activeSession) {
  if (!activeSession) {
    return { session: null, offline: false };
  }

  const breakStart = new Date(activeSession.break_start);
  const breakEnd = new Date();
  const breakSeconds = Math.floor(
    (breakEnd.getTime() - breakStart.getTime()) / 1000,
  );
  const existingBreakSeconds = activeSession.break_seconds || 0;
  const totalBreakSeconds = existingBreakSeconds + breakSeconds;

  const updatedSession = {
    ...activeSession,
    break_active: false,
    break_end: breakEnd.toISOString(),
    break_seconds: totalBreakSeconds,
    total_break_minutes: Math.floor(totalBreakSeconds / 60),
  };

  saveLocalActiveSession(updatedSession);

  const { error } = await supabase
    .from("active_sessions")
    .update({
      break_active: false,
      break_end: breakEnd.toISOString(),
      break_seconds: totalBreakSeconds,
      total_break_minutes: Math.floor(totalBreakSeconds / 60),
    })
    .eq("session_id", activeSession.session_id);

  if (error) {
    devLog("OFFLINE STOP BREAK");
    dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.STOP_BREAK });
    return { session: updatedSession, offline: true };
  }

  const { data } = await supabase
    .from("active_sessions")
    .select("*")
    .eq("session_id", activeSession.session_id)
    .single();

  if (data) {
    saveLocalActiveSession(data);
    dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.STOP_BREAK });
    return { session: data, offline: false };
  }

  dispatchSessionStateChanged({ action: SESSION_STATE_ACTION.STOP_BREAK });
  return { session: updatedSession, offline: false };
}
